import type { DeepReadonly } from "vue";

export type GroupKey = "eecs" | "rego";
export type DomainKey = "fr" | "de" | "se" | "es" | "uk";
export type PpaKey =
  | "fr"
  | "de-nordsee"
  | "de-ostsee"
  | "de-ems"
  | "de-weser"
  | "de-harz"
  | "de-erzgebirge"
  | "se"
  | "es"
  | "uk";
export type Cadence = "monthly" | "quarterly" | "biannual" | "annual";
export type ServiceRoute = "Direct via Soldera" | "Supplier-coordinated";
export type MeterSource =
  | "Connected feed"
  | "Approved upload"
  | "Reported"
  | "Estimated from contract capacity";
export type LotStatus = "Available" | "Cancelled";
/** ok → success, warn → warning, pending → body-secondary, forest → primary (Bootstrap subtle badges). */
export type BadgeTone = "ok" | "warn" | "pending" | "forest";
/** partial: some of the agreement's devices have transferred the month, others have not. */
export type DeliveryState =
  "received" | "partial" | "behind" | "waiting" | "future";
export type CellKind = "feed" | "reported" | "gap" | "future";
/** Semantic fill tokens; `fills.ts` maps them to palette colours. */
export type Fill =
  | "cancelled"
  | "inAccount"
  | "expected"
  | "shortfall"
  | "actualFeed"
  | "actualReported"
  | "estimateOverdue"
  | "estimateFuture"
  | "metered"
  | "estimated"
  | "surplus"
  | "empty";

export interface KeyValue {
  k: string;
  v: string;
}

export interface MeteringPointInput {
  id: string;
  name: string;
  entity: string;
  supplier: string;
  source: MeterSource;
  monthly: number[];
  /** No feed and no readings: every month stays an estimate. */
  alwaysEstimated?: boolean;
  /** Months already reported (0-based exclusive index); overrides the domain default. */
  reportedThrough?: number;
  /** Display identifier when it differs from `id` (edited or hand-added points). */
  mpid?: string;
  /** Created from the Companies tab during the demo. */
  added?: boolean;
}

export interface MeteringPoint extends MeteringPointInput {
  cadence: Cadence;
  /** Months (0-based) whose value came from a confirmed reading rather than the standing estimate. */
  readMonths: readonly number[];
}

export interface CompanyInput {
  name: string;
  site: string;
  beneficiaryType: "Consumer" | "Producer" | "Trader";
  registryCode: string;
  address: string;
  visibility: "Public statement" | "Private statement";
  purpose: string;
  note: string;
}

export interface Earmarked {
  locked: number;
  flexible: number;
  expected: number;
}

export interface FranceMonthInput {
  m: number;
  consumption: number;
  locked: number;
  flexible: number;
  expected: number;
  event: string;
}

export interface DomainInput {
  key: DomainKey;
  group: GroupKey;
  name: string;
  system: "EECS GO" | "UK REGO";
  cadence: Cadence;
  cutoff: string;
  cutoffNote: string;
  rule: string;
  structure: string;
  registry: string;
  registryCode: string; // SUPPORTED_REGISTRIES value, e.g. "FR"
  route: ServiceRoute;
  supply: number;
  isMonthly: boolean;
  note: string;
  /** Only France carries a fixed split; annual domains derive theirs from the allocated lots. */
  earmarked: Earmarked | null;
  meters: MeteringPointInput[];
  companies: CompanyInput[];
}

export interface PpaDeviceInput {
  id: string;
  name: string;
  /** Produced volume per production month (0-based), as far as the seller has reported it. */
  actual: number[];
  /** Production months whose certificates have reached the account (0-based exclusive index). */
  deliveredThrough: number;
  /** Transfer date for a delivered month when it differs from the due date (0-based month index). */
  deliveredOn?: Record<number, string>;
}

export interface PpaInput {
  key: PpaKey;
  group: GroupKey;
  domain: DomainKey;
  name: string;
  counterparty: string;
  cadence: string;
  system: "EECS GO" | "UK REGO";
  terms: string;
  ends: string;
  quarterly: boolean;
  tech: "Solar" | "Wind";
  cc: string;
  registry: string;
  registryCode: string;
  devices: PpaDeviceInput[];
  forecast: number[];
}

export interface CertificateLot {
  id: string;
  ppaKey: PpaKey;
  n: number;
  tech: "Solar" | "Wind";
  /** "2026-MM" */
  month: string;
  status: LotStatus;
  /** e.g. "France · Jan 2026", "Germany · 2026" */
  portfolio: string;
  domainKey: DomainKey;
  cc: string;
  registry: string;
  registryCode: string;
  device: string;
  /** ISO date of the registry transfer deadline */
  deadline: string;
}

export interface StatementFixture {
  id: string;
  company: string;
  site: string;
  registryCode: string;
  address: string;
  meters: string;
  monthIndex: number;
  period: string;
  mwh: number;
  lot: string;
  txn: string;
  cancelled: string;
  received: string;
  verification: string;
}

export interface GroupMeta {
  key: GroupKey;
  title: string;
  shortTitle: string;
  system: "EECS GO" | "UK REGO";
}

export interface YearOption {
  year: number;
  state: "Planning" | "Open" | "No data";
  tone: BadgeTone;
  note: string;
  banner: string;
}

// ---- mutable demo state (owned by the store, consumed by derive) ----

export interface CompanyEdit {
  name?: string;
  registryCode?: string;
  address?: string;
  beneficiaryType?: string;
  visibility?: string;
  purpose?: string;
}

export interface MeterRecord {
  name: string;
  mpid: string;
  supplier: string;
  months: number[];
}

export interface AddedMeter extends MeterRecord {
  uid: string;
  companyName: string;
}

export interface ConsumptionEntry {
  name: string;
  period: string;
  value: string;
}

/** Confirmed readings: domain -> consumption location id -> month index (0-based) -> MWh. */
export type ConfirmedReadings = Partial<
  Record<DomainKey, Record<string, Record<number, number>>>
>;

export interface DemoState {
  requestSubmitted: boolean;
  readings: ConfirmedReadings;
  certMonthFilter: number | null;
  companyEdits: Partial<Record<DomainKey, Record<string, CompanyEdit>>>;
  meterEdits: Partial<Record<DomainKey, Record<string, MeterRecord>>>;
  metersAdded: Partial<Record<DomainKey, AddedMeter[]>>;
  metersRemoved: Partial<Record<DomainKey, string[]>>;
  consumptionEntries: Partial<Record<DomainKey, ConsumptionEntry[]>>;
}

/** What derive functions and components receive: the store hands out its state read-only. */
export type DemoStateView = DeepReadonly<DemoState>;

export const initialDemoState = (): DemoState => ({
  requestSubmitted: false,
  readings: {},
  certMonthFilter: null,
  companyEdits: {},
  meterEdits: {},
  metersAdded: {},
  metersRemoved: {},
  consumptionEntries: {},
});
